import './App.sass';
import { useEffect, useState, useRef, useCallback } from 'react';
import { PrimaryButton, IconButton, FontIcon, TextField, ActionButton } from "@fluentui/react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { Dialog, DialogType, DialogFooter } from '@fluentui/react';
import { useAtom } from 'jotai'
import { authenticationAtom, conversationAtom } from "./State";
import { userAPI } from './UserAPI';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import i18next from "i18next";
import { useCustomerTheme } from './Theme';
import dayjs from 'dayjs';
import { Conversation, Message } from '@the-real-insight/tri-model';
import { ChatExchange } from './components/ChatExchange';

const useStyles = makeStyles({
    flex: {
        display: 'flex',
        marginBottom: '15px',
    },
    flexEnd: {
        display: 'flex',
        justifyContent: 'end',
    },
    location: {
        flexGrow: '1',
        marginRight: '10px',
    },
    title: {
        fontSize: '18px',
        fontWeight: 'bold',
        marginBottom: '10px',
    },
    dummyUX: {
        width: '300px',
        marginTop: '10px',
    },
    messageContainer: {
        display: 'flex',
        //flexGrow: '1',
        marginTop: '20px',
        marginBottom: '20px',
    },
    userMessageContainer: {
        display: 'flex',
        justifyContent: 'end',
        //flexGrow: '1',
        marginTop: '20px',
        marginBottom: '20px',
    },
    chatBotMessage: {
        color: 'black',
        position: 'relative',
        ...shorthands.padding('10px'),
        ...shorthands.borderRadius('5px'),
        marginLeft: '10px',
    },
    chatBotMessageArrow: {
    },
    chatBotSuggestion: {
        backgroundColor: '#9483F1',
        color: 'white',
        flexGrow: '1',
        position: 'relative',
        ...shorthands.padding('10px'),
        ...shorthands.borderRadius('5px'),
        marginLeft: '10px',
        boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    },
    chatBotSuggestionArrow: {
        width: '0',
        height: '0',
        ...shorthands.borderTop('8px', 'solid', 'transparent'),
        ...shorthands.borderBottom('8px', 'solid', 'transparent'),
        ...shorthands.borderRight('8px', 'solid', '#9483F1'),
        position: 'absolute',
        left: '-7px',
        top: '13px',
        zIndex: '999',
    },
    userMessage: {
        backgroundColor: '#DFF0FB',
        color: 'black',
        position: 'relative',
        ...shorthands.padding('10px'),
        ...shorthands.borderRadius('5px'),
        marginRight: '10px',
    },
    userMessageArrow: {
    },
    inputTextField: {
        flexGrow: '1',
        marginRight: '10px'
    },
    microphoneButton: {
        zIndex: '999',
    },
    settingsButton: {
        position: 'absolute',
        right: '10px',
        top: '10px',
    },
    deleteButton: {
        position: 'absolute',
        right: '5px',
        top: '5px',
        opacity: 0.5,
        transition: 'opacity 0.2s ease',
        '&:hover': {
            opacity: 1,
        },
    },
    feedbackButton: {
        opacity: 0.6,
        transition: 'opacity 0.2s ease',
        '&:hover': {
            opacity: 1,
        },
    },
});

interface Properties {
    selectedResource?: any;
    resourceSelected?: (resource: any) => void;
    children?: React.ReactNode;
}

export function ChatBot(properties: Properties) {
    const { theme } = useCustomerTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [authentication] = useAtom(authenticationAtom) as any;
    const [suggestions, setSuggestions] = useState([]) as any;
    const [hideDialog, setHideDialog] = useState(true);
    const [skillAgentResponse, setSkillAgentResponse] = useState() as any;
    const [, setInputFocussed] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [conversations, setConversations] = useState([]) as any;
    const [conversation, setConversation] = useState(null) as any;

    const styles = useStyles();

    const toggleHideDialog = useCallback(() => {
        setHideDialog(!hideDialog);
    }, [hideDialog]);

    const clientActionTitle = (skillAgentResponse: any) => {
        if (!skillAgentResponse || !skillAgentResponse.skill) {
            return 'Client Action';
        }

        switch (skillAgentResponse.skill.id) {
            case 'clientAction/wayfinding': return 'Wayfinding';
            case 'clientAction/showDetails': return 'Resource Details';
            default: return 'Client Action';
        }
    }

    const createConversation = async () => {
        const newConversation = await userAPI.createConversation(authentication.customer);

        setConversation(newConversation);
        setConversations([...conversations, newConversation]);
    }

    const updateConversation = async (conversation: Conversation) => {
        const newConversations = await userAPI.getConversations(authentication.customer);

        console.log('Update conversation >>>', conversation);
        console.log('New conversations >>>', newConversations);

        setConversation(newConversations.find((c: any) => c._id === conversation._id));
        setConversations(newConversations);
    }

    const deleteConversation = async (deletedConversation: any) => {
        // TODO Delete conversation from local storage

        const newConversations = conversations.filter((conversation: any) => conversation._id !== deletedConversation._id);

        setConversations(newConversations);

        if (deletedConversation.id === conversation.id) {
            const newConversation = await userAPI.createConversation(authentication.customer);
            setConversation(newConversation);
            setConversations([...newConversations, newConversation]);
        }

        // Delete 
        await userAPI.deleteConversation(authentication.customer, deletedConversation);
    }

    const truncateText = (text: string, maxLength: number = 50): string => {
        if (!text) return '';

        if (text.length <= maxLength) return text;

        // Find the last space before maxLength
        const truncated = text.substring(0, maxLength);
        const lastSpace = truncated.lastIndexOf(' ');

        // If there's a space, truncate at that position
        if (lastSpace > 0) {
            return text.substring(0, lastSpace) + ' ...';
        }

        // If no space found, just truncate at maxLength
        return truncated + ' ...';
    };

    const filterConversationsByDate = (conversations: any[], filterFn: (date: string) => boolean) => {
        return conversations.filter((conversation: any) => {
            return conversation.messages.length > 0 && filterFn(conversation.messages[0].date);
        });
    };

    const filterConversationsByContent = (conversations: any[], filterText: string) => {
        if (!filterText) return conversations;

        return conversations.filter((conversation: any) => {
            // Check if any message in the conversation contains the filter text
            return conversation.messages.some((message: any) =>
                message.content && message.content.toLowerCase().includes(filterText.toLowerCase())
            );
        });
    };

    useEffect(() => {
        const f = async () => {
            let newConversations = await userAPI.getConversations(authentication.customer);

            console.log('Conversations >>>', newConversations);

            let newConversation = null;

            // TODO Canned call?
            if (newConversations.length === 0) {
                newConversation = await userAPI.createConversation(authentication.customer);
            } else {
                newConversation = newConversations[0];
            }

            setConversations(newConversations);
            setConversation(newConversation);
        };

        f();
    }, []);

    const renderConversations = (title: string, conversations: any[]) => (conversations.length > 0
        ?
        <div>
            <div className='fontWeightBold textSmall marginBottomM'>{title}</div>
            {
                conversations.map((element: any, index: number) => {
                    return (
                        <div key={index} className={`conversation displayFlex gapM alignItemsCenter marginBottomM ${element === conversation ? 'selected' : ''}`}>
                            <div onClick={() => { setConversation(element) }} className='flexGrow1 hoverHand'>{truncateText(element.messages[0].content)}</div>
                            <div><ActionButton className='deleteButton' iconProps={{ iconName: 'Delete' }} onClick={() => { deleteConversation(element) }}></ActionButton></div>
                        </div>
                    );
                })}
        </div>
        :
        <></>
    );

    try {
    return (        
        <div className="chatBot">
            <div className="conversations paddingS">
                <div className="marginBottomM">
                    <TextField
                        placeholder="Konversationen filtern..."
                        value={filterText}
                        onChange={(_, newValue) => setFilterText(newValue || '')}
                        styles={{
                            field: { fontSize: '14px' },
                            fieldGroup: { height: '32px' }
                        }}
                    />
                </div>
                <div>
                    <ActionButton className="marginBottomM" disabled={conversation && conversation.messages.length === 0} iconProps={{ iconName: 'Add' }} onClick={() => createConversation()}>Neue Konversation</ActionButton>
                </div>
                {renderConversations('Heute', filterConversationsByContent(filterConversationsByDate(conversations, (date) =>
                    (dayjs(date).isAfter(dayjs().startOf('day')) || dayjs(date).isSame(dayjs().startOf('day')))
                ), filterText))}
                {renderConversations('Die letzten 7 Tage', filterConversationsByContent(filterConversationsByDate(conversations, (date) =>
                    dayjs(date).isBefore(dayjs().startOf('day')) && (dayjs(date).isAfter(dayjs().subtract(7, 'day').startOf('day')) || dayjs(date).isSame(dayjs().subtract(7, 'day').startOf('day')))
                ), filterText))}
                {renderConversations('Die letzten 30 Tage', filterConversationsByContent(filterConversationsByDate(conversations, (date) =>
                    dayjs(date).isBefore(dayjs().subtract(7, 'day').startOf('day')) && (dayjs(date).isAfter(dayjs().subtract(30, 'day').startOf('day')) || dayjs(date).isSame(dayjs().subtract(30, 'day').startOf('day')))
                ), filterText))}
                {renderConversations(`${dayjs().year()}`, filterConversationsByContent(filterConversationsByDate(conversations, (date) =>
                    dayjs(date).isBefore(dayjs().subtract(30, 'year').startOf('year')) && (dayjs(date).isAfter(dayjs().startOf('year')) || dayjs(date).isSame(dayjs().startOf('year')))
                ), filterText))}
                {renderConversations(`${dayjs().year() - 1} und älter`, filterConversationsByContent(filterConversationsByDate(conversations, (date) =>
                    dayjs(date).isBefore(dayjs().subtract(1, 'year').startOf('year'))
                ), filterText))}
            </div>
            <div className="chatContent">
                <ChatExchange showSuggestions={true}
                    showLogo={true}
                    conversation={conversation}
                    height={'calc(100vh - 150px)'}
                    resourceSelected={properties.resourceSelected}
                    updateConversation={updateConversation}
                    createConversation={createConversation}
                />
            </div>
            <Dialog
                hidden={hideDialog}
                onDismiss={toggleHideDialog}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: clientActionTitle(skillAgentResponse),
                    closeButtonAriaLabel: 'Close',

                }}
                minWidth={600}>
                {skillAgentResponse && skillAgentResponse.skill ?
                    <>
                        {skillAgentResponse.message}
                        {(() => {
                            switch (skillAgentResponse.skill.id) {
                                case 'clientAction/wayfinding': return <img src="./images/navigation.png" alt="navigation" className={styles.dummyUX} />
                                case 'clientAction/showDetails': return <img src="./images/room.png" alt="room" className={styles.dummyUX} />
                                default: return <></>
                            }
                        })()}</>
                    :
                    <></>}
                <DialogFooter>
                    <PrimaryButton onClick={toggleHideDialog} text="Close" />
                </DialogFooter>
            </Dialog>
        </div >);
    } catch (error) {
        console.error('Error in ChatBot:', error);
        return <div>Error loading chatbot</div>;
    }
}